<template>
  <div class="help-menu-panel" role="menu" aria-label="Help" data-testid="viewer-help-menu-panel">
    <p class="help-menu-panel__heading">HELP</p>
    <a
      v-for="(link, index) in helpMenuLinks"
      :key="link.id"
      class="help-menu-panel__item"
      role="menuitem"
      :href="link.href"
      :target="link.kind === 'external' ? '_blank' : undefined"
      :rel="link.kind === 'external' ? 'noopener noreferrer' : undefined"
      :data-testid="`viewer-help-menu-link-${link.id}`"
      @click="$emit('activate', link)"
      @keydown="$emit('keydown', $event, index)"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path :d="iconPaths[link.icon]" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      <span class="help-menu-panel__label">{{ link.label }}</span>
    </a>
  </div>
</template>

<script setup lang="ts">
import { helpMenuLinks, type HelpMenuLink } from './helpMenuLinks';

defineEmits<{
  activate: [HelpMenuLink];
  keydown: [KeyboardEvent, number];
}>();

const iconPaths: Record<HelpMenuLink['icon'], string> = {
  gift: 'M20 12v8H4v-8m8-8v16M2 8h20v4H2zm10 0H6.5a2.5 2.5 0 1 1 2.5-2.5V8Zm0 0h5.5a2.5 2.5 0 1 0-2.5-2.5V8Z',
  video: 'm22 8-6 4 6 4V8ZM2 6h14v12H2z',
  book: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z',
  bug: 'm8 2 1.5 3h5L16 2M9 12h6M8 7h8a3 3 0 0 1 3 3v6a5 5 0 0 1-10 0v-6a3 3 0 0 1 3-3ZM5 9l3 2m11-2-3 2M5 15l3-1m11 1-3-1',
  webinar: 'M4 4h16v12H4zM8 20h8m-4-4v4M8 8h8m-8 3h5',
  'monitor-down': 'M4 4h16v11H4zM8 20h8m-4-5v5m0-10v6m0 0 3-3m-3 3-3-3',
  message: 'M21 11.5a8.4 8.4 0 0 1-9 8.5 9.5 9.5 0 0 1-4-.9L3 21l1.7-4.4A8.2 8.2 0 0 1 3 11.5a8.5 8.5 0 0 1 9-8.5 8.5 8.5 0 0 1 9 8.5Z',
};
</script>

<style scoped>
.help-menu-panel {
  @apply w-[240px] rounded-10 border border-slate-200 bg-white p-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
  animation: help-menu-enter 150ms ease;
}

.help-menu-panel__heading {
  @apply m-0 px-2.5 pt-1.5 pb-2 text-xs font-bold leading-4 tracking-[0.02em] text-slate-500 dark:text-zinc-400;
}

.help-menu-panel__item {
  @apply flex items-center gap-3 rounded-[8px] px-2.5 py-2.5 text-sm font-medium leading-[18px] text-slate-700 dark:text-zinc-200;
  @apply hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50;
}

.help-menu-panel__label {
  @apply flex-1;
}

@keyframes help-menu-enter {
  from {
    opacity: 0;
    transform: translateX(-4px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .help-menu-panel {
    animation: none;
  }
}
</style>
